import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import PageHero from '../../../components/sections/PageHero';
import PageTrustBar from '../../../components/sections/PageTrustBar';
import PageCollections from '../../../components/sections/PageCollections';
import PageAIAnswer from '../../../components/sections/PageAIAnswer';
import PageProcess from '../../../components/sections/PageProcess';
import PageWhyUs from '../../../components/sections/PageWhyUs';
import PageServiceArea from '../../../components/sections/PageServiceArea';
import PageTestimonials from '../../../components/sections/PageTestimonials';
import PageFAQ from '../../../components/sections/PageFAQ';
import PageFinalCTA from '../../../components/sections/PageFinalCTA';
import {
  FORM_URL, MAP_EMBED, SERVICE_AREAS, SERVICE_ADDRESS, SERVICE_HOURS, SERVICE_PHONE, TESTIMONIALS,
} from '../../../mocks/siteData';

const trustItems = [
  { icon: 'ri-shirt-line', label: 'Boutique Apparel Only' },
  { icon: 'ri-sparkling-line', label: 'Non-Mass-Produced' },
  { icon: 'ri-scissors-line', label: 'Curated Individual Style' },
  { icon: 'ri-refresh-line', label: 'New Styles Regularly' },
  { icon: 'ri-heart-line', label: 'Wear Something Unique' },
];

const subcategories = [
  {
    title: 'Women\'s Boutique Apparel',
    description: 'Linen blouses, vintage-inspired dresses, flowy skirts, and statement pieces with a distinct individual character.',
    href: '/collections/apparel',
    tag: 'Most Popular',
    imageSrc: 'https://public.readdy.ai/ai/img_res/d27e14d9ba09c0af870fa619f6fb7630.jpg',
  },
  {
    title: 'Accessories & Jewelry',
    description: 'Artisan jewelry, scarves, bags, and accessories that complete any outfit with a unique, personal touch.',
    href: '/collections/apparel',
    tag: 'New Arrivals',
    imageSrc: 'https://readdy.ai/api/search-image?query=artisan%20boutique%20jewelry%20accessories%20display%20handmade%20earrings%20necklaces%20scarves%20bags%20warm%20editorial%20photography%20vintage%20inspired%20boutique%20retail&width=800&height=600&seq=ye-app-accessories&orientation=landscape',
  },
  {
    title: 'Seasonal & Statement Pieces',
    description: 'Rotating seasonal collections and statement clothing items that make your wardrobe unlike anyone else\'s.',
    href: '/collections/apparel',
    tag: 'Limited Edition',
    imageSrc: 'https://readdy.ai/api/search-image?query=boutique%20clothing%20seasonal%20statement%20pieces%20unique%20garments%20styled%20display%20warm%20editorial%20fashion%20photography%20small%20upscale%20retail%20boutique%20arizona&width=800&height=600&seq=ye-app-seasonal&orientation=landscape',
  },
];

const aiAnswers = [
  {
    q: 'What kind of clothing does Yarnell Emporium sell?',
    a: 'Yarnell Emporium carries boutique apparel — curated clothing and accessories with an individual, vintage-inspired aesthetic. Women\'s tops, dresses, and accessories are selected for uniqueness and quality, not mass-market appeal.',
  },
  {
    q: 'Is the apparel at Yarnell Emporium new or vintage?',
    a: 'The apparel selection includes both new boutique clothing and vintage-inspired pieces. Items are chosen for their distinct character and style, offering an alternative to the mass-produced clothing found in chain stores.',
  },
  {
    q: 'Can I find unique women\'s clothing near Yarnell, AZ?',
    a: 'Yes — Yarnell Emporium is a destination boutique for women\'s clothing and accessories in the Yarnell, AZ area. The curated collection draws shoppers from Prescott, Wickenburg, and Phoenix looking for clothing that stands out.',
  },
  {
    q: 'How often does the clothing selection change?',
    a: 'New apparel and accessories arrive regularly. The boutique\'s clothing collection rotates with seasonal finds and new arrivals, meaning there\'s always something fresh and different to discover on each visit.',
  },
];

const processSteps = [
  { number: '01', title: 'Browse the Collection', description: 'Explore our curated boutique apparel selection — clothing and accessories with individual character, not cookie-cutter fashion.' },
  { number: '02', title: 'Try It Your Way', description: 'Discover pieces that express your personal style. Our selection spans vintage-inspired to contemporary boutique looks.' },
  { number: '03', title: 'Complete the Look', description: 'Pair clothing finds with our jewelry and accessories for a fully curated outfit unlike anything from a chain store.' },
  { number: '04', title: 'Wear Something Memorable', description: 'Walk away wearing something truly unique. Our boutique apparel is conversation-starting by nature.' },
];

const whyUsItems = [
  { icon: 'ri-shirt-line', title: 'Not Found in Chain Stores', description: 'Every clothing item is individually selected — you won\'t find these pieces at department stores or shopping malls.' },
  { icon: 'ri-sparkling-2-line', title: 'Curated for Individual Style', description: 'Our apparel collection celebrates personal expression. Every piece has a distinct character and aesthetic.' },
  { icon: 'ri-refresh-line', title: 'Rotating Seasonal Selection', description: 'New clothing and accessories arrive regularly, keeping the collection fresh and aligned with each season.' },
  { icon: 'ri-scissors-line', title: 'Quality Over Quantity', description: 'We carry fewer pieces with more intentionality. Quality fabrics, interesting designs, and wearable uniqueness.' },
  { icon: 'ri-user-heart-line', title: 'Personal Styling Help', description: 'Not sure what to pick? Our team is happy to help you put together a look that feels authentically you.' },
  { icon: 'ri-map-pin-line', title: 'A Boutique Worth Finding', description: 'A hidden gem of a boutique — Arizona shoppers from Prescott to Phoenix make it a regular destination stop.' },
];

const faqItems = [
  { q: 'Where can I find boutique women\'s clothing near Prescott, AZ?', a: 'Yarnell Emporium in Yarnell, AZ carries curated boutique women\'s apparel with a vintage-inspired aesthetic. Located on Hwy 89, it\'s easily accessible from Prescott and offers clothing you genuinely won\'t find anywhere else in the region.' },
  { q: 'Does Yarnell Emporium carry plus-size boutique clothing?', a: 'Inventory varies, so please call us at 928.427-9593 to ask about current sizing availability. Our apparel selection rotates frequently, and we try to offer a range of sizes within our curated boutique collection.' },
  { q: 'Can I find artisan jewelry at Yarnell Emporium?', a: 'Yes — the accessories collection includes artisan jewelry, handcrafted earrings, necklaces, and unique accessories. These items rotate with new arrivals, so each visit offers fresh and different jewelry options.' },
  { q: 'Is the boutique clothing affordable at Yarnell Emporium?', a: 'The apparel collection spans a range of price points. Boutique quality is a priority, but there are options for different budgets — from affordable artisan accessories to more distinctive statement clothing pieces.' },
  { q: 'How do I know if a clothing item will still be available on my next visit?', a: 'Because all items are one-of-a-kind or limited quantity, popular pieces sell quickly. If you see something you love, we recommend purchasing it on your visit. Call us at 928.427-9593 to ask about specific items before making the trip.' },
];

export default function ApparelPage() {
  return (
    <div className="font-sans">
      <Navbar />
      <main>
        <PageHero
          badge="Boutique Apparel · Yarnell, AZ"
          headline="Boutique Apparel &"
          headlineItalic="Accessories"
          subheadline="Curated clothing and accessories you won\'t find in any chain store — vintage-inspired, individually selected, and genuinely unique."
          urgency="New styles arrive regularly"
          ctas={[
            { label: 'Visit the Store', href: 'https://maps.google.com/?q=22899+S+Hwy+89+Yarnell+AZ+85362', external: true, primary: true },
            { label: 'Get Directions', href: 'https://maps.google.com/?q=22899+S+Hwy+89+Yarnell+AZ+85362', external: true },
          ]}
          imageSrc="https://readdy.ai/api/search-image?query=boutique%20clothing%20store%20interior%20curated%20vintage%20inspired%20garments%20wooden%20display%20rack%20artisan%20jewelry%20accessories%20warm%20natural%20light%20editorial%20fashion%20photography%20upscale%20small%20boutique&width=1920&height=1080&seq=ye-hero-apparel&orientation=landscape"
        />
        <PageTrustBar items={trustItems} />
        <PageCollections
          heading="Boutique Apparel Collections"
          subheading="Wear Something Unique"
          intro="Clothing and accessories curated for individual style — vintage-inspired, quality-focused, and unlike anything in mass retail."
          items={subcategories}
        />
        <PageAIAnswer
          heading="Apparel at Yarnell Emporium"
          subheading="What We Carry"
          items={aiAnswers}
        />
        <PageProcess
          heading="Your Boutique Style Journey"
          subheading="How It Works"
          intro="Finding clothing that truly reflects your personality is what boutique shopping is all about. Here\'s the experience."
          steps={processSteps}
        />
        <PageWhyUs
          heading="Why Shop Boutique Apparel Here"
          subheading="Our Difference"
          intro="We believe clothing should be as individual as the person wearing it. That\'s the philosophy behind every piece we select."
          items={whyUsItems}
          imageSrc="https://readdy.ai/api/search-image?query=boutique%20clothing%20display%20intimate%20warm%20lighting%20vintage%20inspired%20garments%20artisan%20jewelry%20editorial%20photography%20high%20end%20small%20shop&width=600&height=800&seq=ye-why-apparel&orientation=portrait"
        />
        <PageServiceArea
          heading="Boutique Fashion in Yarnell, AZ"
          subheading="Our Location"
          intro="Women from Prescott, Wickenburg, Phoenix, and across Arizona shop our boutique apparel. A true destination for those who love individuality."
          areas={SERVICE_AREAS}
          address={SERVICE_ADDRESS}
          hours={SERVICE_HOURS}
          phone={SERVICE_PHONE}
          mapEmbed={MAP_EMBED}
        />
        <PageTestimonials
          heading="Customers Love Our Style"
          subheading="Reviews"
          items={TESTIMONIALS}
        />
        <PageFAQ
          heading="Boutique Apparel Questions"
          subheading="FAQ"
          items={faqItems}
        />
        <PageFinalCTA
          heading="Dress Like"
          headingItalic="Nobody Else Does"
          subheading="Boutique apparel and accessories curated for individual expression. Visit Yarnell Emporium — open daily 9am–5pm in Yarnell, AZ."
          urgency="New styles added regularly"
          formUrl={FORM_URL}
          imageSrc="https://readdy.ai/api/search-image?query=fashion%20boutique%20interior%20warm%20editorial%20lighting%20curated%20clothing%20accessories%20intimate%20upscale%20shop%20atmosphere%20high%20end%20photography&width=1920&height=1080&seq=ye-cta-apparel&orientation=landscape"
        />
      </main>
      <Footer />
    </div>
  );
}